import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import fromUnixTime from 'date-fns/fromUnixTime';
import format from 'date-fns/format';
import { downloadCsvFile } from './downloadHelper';

const DATE_FORMAT_BY_GROUP = {
  hour: 'dd/MM/yyyy HH:mm',
  day: 'dd/MM/yyyy',
  week: 'dd/MM/yyyy',
  month: 'MM/yyyy',
  year: 'yyyy',
};

const formatPeriodLabel = (timestamp, groupBy) =>
  format(
    fromUnixTime(timestamp),
    DATE_FORMAT_BY_GROUP[groupBy] || 'dd/MM/yyyy'
  );

// Groupings that collapse the filename to a single date instead of a range.
const FILE_NAME_DATE_FORMAT_BY_GROUP = {
  month: 'yyyyMM',
  year: 'yyyy',
};

/**
 * Turns an account name into a filename-safe slug: accents are stripped and any
 * run of non-alphanumeric characters collapses into a single hyphen.
 * "Soluções AMF & Cia" -> "solucoes-amf-cia"
 */
const slugifyAccountName = name =>
  String(name || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Builds the export filename as Relatorio_Mensagens_[conta]_[periodo].[ext].
 * The period is yyyyMM / yyyy when the report is grouped by month or year, and
 * the yyyyMMdd-yyyyMMdd range otherwise. The date pattern is fixed rather than
 * locale-aware so the same period always yields the same filename.
 */
export const buildMessageReportFileName = ({
  accountName,
  from,
  to,
  groupBy = 'day',
  extension,
}) => {
  const singleDateFormat = FILE_NAME_DATE_FORMAT_BY_GROUP[groupBy];
  const period = singleDateFormat
    ? format(fromUnixTime(to), singleDateFormat)
    : [from, to].map(ts => format(fromUnixTime(ts), 'yyyyMMdd')).join('-');

  const parts = [
    'Relatorio_Mensagens',
    slugifyAccountName(accountName),
    period,
  ].filter(Boolean);

  return `${parts.join('_')}.${extension}`;
};

/**
 * Merges the store timeseries (received / sent / undelivered / api / ia /
 * automated / template) into one row per period. Two orthogonal breakdowns:
 *  - Origin (partitions "sent"): viaAttendant / viaIa / viaApi / viaAutomated,
 *    where viaAttendant (human panel) = sent - ia - api - automated (remainder).
 *  - Format (overlaps origin): template / session (= sent - template).
 * Delivered = sent - undelivered. No extra API call is needed.
 */
export const buildMessageReportData = ({
  incoming = [],
  outgoing = [],
  undelivered = [],
  api = [],
  ia = [],
  automated = [],
  template = [],
  groupBy = 'day',
} = {}) => {
  const byTimestamp = new Map();

  const collect = (series, key) => {
    series.forEach(({ timestamp, value }) => {
      const entry = byTimestamp.get(timestamp) || {
        received: 0,
        sent: 0,
        undelivered: 0,
        api: 0,
        ia: 0,
        automated: 0,
        template: 0,
      };
      entry[key] = Number(value) || 0;
      byTimestamp.set(timestamp, entry);
    });
  };
  collect(incoming, 'received');
  collect(outgoing, 'sent');
  collect(undelivered, 'undelivered');
  collect(api, 'api');
  collect(ia, 'ia');
  collect(automated, 'automated');
  collect(template, 'template');

  const rows = [...byTimestamp.keys()]
    .sort((a, b) => a - b)
    .map(timestamp => {
      const {
        received,
        sent,
        undelivered: notDelivered,
        api: viaApi,
        ia: viaIa,
        automated: viaAutomated,
        template: templateCount,
      } = byTimestamp.get(timestamp);
      return {
        label: formatPeriodLabel(timestamp, groupBy),
        received,
        sent,
        delivered: Math.max(sent - notDelivered, 0),
        viaAttendant: Math.max(sent - viaIa - viaApi - viaAutomated, 0),
        viaIa,
        viaApi,
        viaAutomated,
        template: templateCount,
        session: Math.max(sent - templateCount, 0),
        total: received + sent,
      };
    });

  const sumKeys = [
    'received',
    'sent',
    'delivered',
    'viaAttendant',
    'viaIa',
    'viaApi',
    'viaAutomated',
    'template',
    'session',
  ];
  const totals = rows.reduce(
    (acc, row) => {
      sumKeys.forEach(k => {
        acc[k] += row[k];
      });
      return acc;
    },
    Object.fromEntries(sumKeys.map(k => [k, 0]))
  );

  return { rows, totals: { ...totals, total: totals.received + totals.sent } };
};

// Numeric columns in display order (after the period/label column). Keys match
// both the row/totals objects (buildMessageReportData) and the labels object.
const REPORT_COLUMNS = [
  'received',
  'sent',
  'delivered',
  'viaAttendant',
  'viaIa',
  'viaApi',
  'viaAutomated',
  'template',
  'session',
  'total',
];

const escapeCsvCell = cell => {
  const value = cell === null || cell === undefined ? '' : String(cell);
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
};

export const exportMessageReportCsv = ({
  rows,
  totals,
  labels,
  account,
  period,
  generatedAt,
  fileName,
}) => {
  const headerLines = [account, period, generatedAt]
    .filter(Boolean)
    .map(text => [text]);

  const lines = [
    ...headerLines,
    [],
    [labels.date, ...REPORT_COLUMNS.map(k => labels[k])],
    ...rows.map(row => [row.label, ...REPORT_COLUMNS.map(k => row[k])]),
    [labels.totalRow, ...REPORT_COLUMNS.map(k => totals[k])],
  ];
  const content = lines
    .map(line => line.map(escapeCsvCell).join(','))
    .join('\n');
  downloadCsvFile(fileName, content);
};

/**
 * Rasterizes the brand logo (SVG or raster) to a PNG data URL so jsPDF can embed
 * it. SVGs without intrinsic dimensions are patched with an explicit size to
 * guarantee a canvas render size. Returns null (and the PDF renders without a
 * logo) if the asset can't be loaded.
 */
const loadLogoDataUrl = async (logoUrl, size = 220) => {
  if (!logoUrl) return null;
  try {
    const response = await fetch(logoUrl);
    if (!response.ok) return null;

    const contentType = response.headers.get('content-type') || '';
    const isSvg = contentType.includes('svg') || /\.svg(\?|$)/i.test(logoUrl);

    let objectUrl;
    if (isSvg) {
      let svg = await response.text();
      if (!/<svg[^>]*\swidth=/.test(svg)) {
        svg = svg.replace(/<svg\b/, `<svg width="${size}" height="${size}"`);
      }
      objectUrl = URL.createObjectURL(
        new Blob([svg], { type: 'image/svg+xml' })
      );
    } else {
      objectUrl = URL.createObjectURL(await response.blob());
    }

    const result = await new Promise(resolve => {
      const image = new Image();
      image.onload = () => {
        const width = image.naturalWidth || size;
        const height = image.naturalHeight || size;
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(image, 0, 0, width, height);
        resolve({ dataUrl: canvas.toDataURL('image/png'), width, height });
      };
      image.onerror = () => resolve(null);
      image.src = objectUrl;
    });

    URL.revokeObjectURL(objectUrl);
    return result;
  } catch {
    return null;
  }
};

const BRAND_BLUE = [39, 129, 246]; // #2781F6 — n.brand
const SLATE_DARK = [15, 23, 42];
const SLATE_MUTED = [100, 116, 139];
const SLATE_BORDER = [226, 232, 240];
const SLATE_FOOT = [241, 245, 249];

const formatNumber = value => Number(value || 0).toLocaleString();

export const exportMessageReportPdf = async ({
  rows,
  totals,
  labels,
  title,
  account,
  period,
  generatedAt,
  footerTotal,
  fileName,
  logoUrl,
}) => {
  // landscape: a tabela tem muitas colunas (recebidas/enviadas/entregues +
  // origem API/Agente/Automatizadas + total) e não caberia bem em retrato.
  // eslint-disable-next-line new-cap
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const marginX = 40;
  let cursorY = 44;

  const logo = await loadLogoDataUrl(logoUrl);
  if (logo) {
    const renderWidth = 70;
    const renderHeight = (logo.height / logo.width) * renderWidth;
    doc.addImage(
      logo.dataUrl,
      'PNG',
      marginX,
      cursorY,
      renderWidth,
      renderHeight
    );
    cursorY += renderHeight + 20;
  }

  // Document header: title + account + period + generation date/time
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...SLATE_DARK);
  doc.text(title, marginX, cursorY);
  cursorY += 18;

  if (account) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...SLATE_DARK);
    doc.text(account, marginX, cursorY);
    cursorY += 14;
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...SLATE_MUTED);
  doc.text(period, marginX, cursorY);
  cursorY += 13;

  if (generatedAt) {
    doc.setFontSize(9);
    doc.text(generatedAt, marginX, cursorY);
    cursorY += 4;
  }

  // right-align every numeric column (0 = period label, left)
  const columnStyles = { 0: { halign: 'left' } };
  REPORT_COLUMNS.forEach((_, i) => {
    columnStyles[i + 1] = { halign: 'right' };
  });

  autoTable(doc, {
    startY: cursorY + 16,
    margin: { left: marginX, right: marginX },
    head: [[labels.date, ...REPORT_COLUMNS.map(k => labels[k])]],
    body: rows.map(row => [
      row.label,
      ...REPORT_COLUMNS.map(k => formatNumber(row[k])),
    ]),
    foot: [
      [labels.totalRow, ...REPORT_COLUMNS.map(k => formatNumber(totals[k]))],
    ],
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 4, lineColor: SLATE_BORDER },
    headStyles: { fillColor: BRAND_BLUE, textColor: 255, fontStyle: 'bold' },
    footStyles: {
      fillColor: SLATE_FOOT,
      textColor: SLATE_DARK,
      fontStyle: 'bold',
    },
    columnStyles,
  });

  // Document footer: grand total for the period
  if (footerTotal) {
    const finalY = doc.lastAutoTable?.finalY || cursorY;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...SLATE_DARK);
    doc.text(footerTotal, marginX, finalY + 22);
  }

  doc.save(fileName);
};
